import { Button, Collapse, Input, Select, Toggle } from '@appd/uik';
import { CSSProperties, useEffect, useState } from 'react';
import { capitalizeFirstLetter } from '../../../../../utils';
import { VisualizationPropertiesPanelProps } from '../../models';
import { getJsonataExpressionForVariable } from '../../utils';
import { getStyles } from '../getStyles';

export const LegendType = {
  HIDDEN: 'hidden',
  RIGHT: 'right',
  LEFT: 'left',
} as const;
export type LegendTypeOption = (typeof LegendType)[keyof typeof LegendType];

export const TooltipType = {
  HIDDEN: 'hidden',
  SINGLE: 'single',
} as const;
export type TooltipTypeOption = (typeof TooltipType)[keyof typeof TooltipType];

const { COLLAPSE_PANEL, NUMBER_INPUT, PANEL, PROPERTY, PROPERTY_LABEL, TOGGLE_BUTTON } = getStyles();

export const HeatGridPropertiesPanel = ({
  props,
  updateProps,
  expressions,
}: VisualizationPropertiesPanelProps): JSX.Element => {
  const [data, setData] = useState<string>(props.data);
  const [unit, setUnit] = useState<string>(props.unit ?? '');
  const [legendType, setLegendType] = useState<LegendTypeOption>(props.legendType);
  const [legendNumDecimals, setLegendNumDecimals] = useState<number>(props.legendNumDecimals);
  const [tooltipType, setTooltipType] = useState<TooltipTypeOption>(props.tooltipType);
  const [tooltipNumDecimals, setTooltipNumDecimals] = useState<number>(props.tooltipNumDecimals);

  useEffect(() => {
    setData(props.data);
    setUnit(props.unit ?? '');
    setLegendType(props.legendType);
    setLegendNumDecimals(props.legendNumDecimals);
    setTooltipType(props.tooltipType);
    setTooltipNumDecimals(props.tooltipNumDecimals);
  }, [props]);

  return (
    <div key="heatGridProperties" className={PANEL as string}>
      <Collapse.Group open={[true]}>
        <Collapse title="Data binding">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <Select
              label="Main data set"
              value={[expressions.findIndex(variable => getJsonataExpressionForVariable(variable) === data)]}
              onChange={(value: number[]) => {
                const expressionVariable = expressions[value[0]];
                const expression = getJsonataExpressionForVariable(expressionVariable);
                setData(expression);
                updateProps({
                  data: expression,
                });
              }}
              showClear={false}
              required
            >
              {expressions.map(expression => (
                <Select.Option key={expression}>{expression}</Select.Option>
              ))}
            </Select>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Visualization">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <Input
              key="unit"
              label="Unit"
              value={unit}
              onChange={e => {
                setUnit(e.target.value);
                updateProps({
                  unit: e.target.value,
                });
              }}
            />
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Legend">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <div key="layout" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Layout</div>
              <Toggle
                selected={[Object.values(LegendType).findIndex(value => value === legendType)]}
                onSelectedChange={selected => {
                  const option = Object.values(LegendType)[selected[0]] as LegendTypeOption;
                  setLegendType(option);
                  updateProps({
                    legendType: option,
                  });
                }}
              >
                {Object.values(LegendType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
            <Input
              key="legendNumDecimals"
              label="Number of decimals"
              type="number"
              value={legendNumDecimals}
              onChange={e => {
                setLegendNumDecimals(Number(e.target.value));
                updateProps({
                  legendNumDecimals: Number(e.target.value),
                });
              }}
              min={0}
              max={9}
              required={true}
              style={NUMBER_INPUT as CSSProperties}
            />
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
      <Collapse.Group open={[true]}>
        <Collapse title="Tooltip">
          <Collapse.Panel className={COLLAPSE_PANEL}>
            <div key="configuration" className={PROPERTY as string}>
              <div className={PROPERTY_LABEL as string}>Configuration</div>
              <Toggle
                selected={[Object.values(TooltipType).findIndex(value => value === tooltipType)]}
                onSelectedChange={selected => {
                  const option = Object.values(TooltipType)[selected[0]] as TooltipTypeOption;
                  setTooltipType(option);
                  updateProps({
                    tooltipType: option,
                  });
                }}
              >
                {Object.values(TooltipType).map(option => (
                  <Button key={option} className={TOGGLE_BUTTON}>
                    {capitalizeFirstLetter(option)}
                  </Button>
                ))}
              </Toggle>
            </div>
            <Input
              key="tooltipNumDecimals"
              label="Number of decimals"
              type="number"
              value={tooltipNumDecimals}
              onChange={e => {
                setTooltipNumDecimals(Number(e.target.value));
                updateProps({
                  tooltipNumDecimals: Number(e.target.value),
                });
              }}
              min={0}
              max={9}
              required={true}
              style={NUMBER_INPUT as CSSProperties}
            />
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
    </div>
  );
};
